import { fetchCanonicalSnippets } from '../../../components/messaging/canonicalMessagingApi';
import { registerSessionReset, sessionTicket, sessionIsCurrent } from '../../../utils/sessionLifecycle';
import { quickReplyMedia } from './quickReplyMedia';
const EMPTY = Object.freeze({ items: [], loaded: false, loading: false, error: null });
export function createQuickReplyStore({ fetch = fetchCanonicalSnippets, media = quickReplyMedia } = {}) {
  const channels = new Map(), listeners = new Set(), uses = new Set(); let epoch = 0;
  const snapshot = channel => channels.get(channel)?.state || EMPTY;
  const emit = () => listeners.forEach(fn => fn());
  const entry = channel => { if (!channels.has(channel)) channels.set(channel, { state: EMPTY, flight: null, queued: false }); return channels.get(channel); };
  const load = (channel, force = false) => {
    const row = entry(channel);
    if (row.flight) { if (force) row.queued = true; return row.flight; }
    if (!force && row.state.loaded) return Promise.resolve(row.state.items);
    const run = epoch, ticket = sessionTicket();
    row.state = { ...row.state, loading: !row.state.loaded, error: null }; emit();
    row.flight = Promise.resolve().then(() => fetch(channel)).then(items => {
      if (run !== epoch || !sessionIsCurrent(ticket)) return [];
      if(!Array.isArray(items))throw new Error('Could not load quick replies.');
      row.state = { ...row.state, items, loaded: true, loading: false, error: null }; media.reconcile(channel, items); emit(); return items;
    }).catch(error => {
      if (run === epoch && sessionIsCurrent(ticket)) { row.state = { ...row.state, loading: false, error: row.state.loaded ? null : error }; emit(); }
      throw error;
    }).finally(() => { row.flight = null; if (run === epoch && row.queued) { row.queued = false; load(channel, true).catch(() => {}); } });
    return row.flight;
  };
  const apply = (channel, item, removed = false) => {
    if (!item?.id) return;
    const row = entry(channel), items = row.state.items.filter(old => old.id !== item.id);
    if (!removed) items.push(item);
    items.sort((a,b) => (b.usage_count || 0)-(a.usage_count || 0) || String(a.title).localeCompare(String(b.title)));
    row.state = { ...row.state, items }; media.reconcile(channel,items); emit();
  };
  return { snapshot, load, find: id => { for(const row of channels.values()){const item=row.state.items.find(i=>i.id===id);if(item)return item;} return null; }, subscribe: fn => { listeners.add(fn); return () => listeners.delete(fn); },
    prime: accounts => accounts.forEach(account => {const row=entry(account.id);row.state={...row.state,channelName:account.display_name};emit();load(account.id).catch(() => {});}),
    reloadAll: () => channels.forEach((_,channel) => load(channel,true).catch(() => {})),
    apply, used: ({ snippet_id:id, snippet_use_key:key } = {}) => {
      if (!id || !key || uses.has(key)) return; uses.add(key);
      channels.forEach((row,channel) => { const item=row.state.items.find(i=>i.id===id); if(item)apply(channel,{...item,usage_count:(item.usage_count||0)+1}); });
    }, reset: () => { epoch++; channels.clear(); uses.clear(); media.clear(); emit(); },
  };
}
export const quickReplyStore = createQuickReplyStore();
registerSessionReset(() => quickReplyStore.reset());
export const getQuickReplies = channel => quickReplyStore.load(channel);
